import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';

export type PlatformBillingModel = 'per_unit_monthly' | 'usage';

export interface PlatformBillingRate {
  metric_key: string;
  label_en: string;
  label_da: string;
  description_en: string | null;
  description_da: string | null;
  unit_price_cents: number;
  currency: string;
  billing_model: PlatformBillingModel;
  enabled: boolean;
  sort_order: number;
  updated_at: string | null;
}

export interface BillingLineItem {
  metric_key: string;
  quantity: number;
  unit_price_cents: number;
  subtotal_cents: number;
  enabled: boolean;
}

export interface BillingAccountClinic {
  id: string;
  name: string;
  is_sub_clinic: boolean;
  is_active: boolean;
  parent_clinic_id: string | null;
}

export interface BillingAccount {
  billing_account_id: string;
  root_clinic_name: string;
  root_clinic_slug: string;
  subscription_status: string;
  clinic_count: number;
  sub_clinic_count: number;
  employee_count: number;
  clinics: BillingAccountClinic[];
  overrides: Record<string, number>;
  effective_rates: Record<string, number>;
  line_items: BillingLineItem[];
  estimated_monthly_cents: number;
}

export interface PlatformBillingUsage {
  rates: PlatformBillingRate[];
  platform_totals: {
    billing_accounts: number;
    clinic_count: number;
    employee_count: number;
    estimated_monthly_cents: number;
  };
  accounts: BillingAccount[];
}

const USAGE_QUERY_KEY = ['platform-billing-usage'];
const RATES_QUERY_KEY = ['platform-billing-rates'];

export function usePlatformBillingUsage() {
  return useQuery({
    queryKey: USAGE_QUERY_KEY,
    queryFn: async (): Promise<PlatformBillingUsage> => {
      const { data, error } = await supabase.rpc('get_platform_billing_usage');
      if (error) throw error;
      return data as unknown as PlatformBillingUsage;
    },
  });
}

export function useUpdatePlatformBillingRate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: {
      metric_key: string;
      unit_price_cents?: number;
      enabled?: boolean;
    }) => {
      const { data, error } = await supabase.rpc('update_platform_billing_rate', {
        p_metric_key: params.metric_key,
        p_unit_price_cents: params.unit_price_cents,
        p_enabled: params.enabled,
      });
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: USAGE_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: RATES_QUERY_KEY });
    },
  });
}

export function useSetClinicBillingRateOverride() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: {
      billing_account_id: string;
      metric_key: string;
      unit_price_cents: number | null;
    }) => {
      const { data, error } = await supabase.rpc('set_clinic_billing_rate_override', {
        p_billing_account_id: params.billing_account_id,
        p_metric_key: params.metric_key,
        p_unit_price_cents: params.unit_price_cents ?? undefined,
      });
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: USAGE_QUERY_KEY });
    },
  });
}

export function formatDkkFromCents(cents: number): string {
  return new Intl.NumberFormat('da-DK', {
    style: 'currency',
    currency: 'DKK',
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(cents / 100);
}

export function parseDkkInputToCents(value: string): number | null {
  const trimmed = value.trim();
  if (!trimmed) return null;

  const normalized = trimmed.replace(/\s/g, '').replace(/\./g, '').replace(',', '.');
  const parsed = Number(normalized);
  if (!Number.isFinite(parsed) || parsed < 0) return null;

  return Math.round(parsed * 100);
}

export function centsToDkkInput(cents: number): string {
  return (cents / 100).toFixed(2).replace('.', ',');
}
